import React, {createRef} from "react";
import Table, { BaseTableProps, Column, Column as BaseColumn } from "react-base-table";

import "react-base-table/styles.css";
import "./BaseTable.scss";

import LoadingStar from "../loaders/LoadingStar";
import VirtualScrolbar from "./VirtualScrolbar";

/**
 * react-base-table compares column definitions ignoring functions
 * (`ignoreFunctionInColumnCompare`), so a column whose `render` changed —
 * say a count that arrives after the rows did — kept rendering with the old
 * one. A stable id per render function makes that change visible to it.
 */
const renderIds = new WeakMap<Function, number>();
let nextRenderId = 0;
function renderId(fn?: Function): number | undefined {
  if (typeof fn !== "function") return undefined;
  let id = renderIds.get(fn);
  if (id === undefined) {
    id = ++nextRenderId;
    renderIds.set(fn, id);
  }
  return id;
}

function TableCell( props: any ) {

  const
      rowData = props.rowData,
      text = rowData[props.column.dataIndex],
      record = rowData;

  if ( props.render ) {
    return props.render(text, record);
  }

  return text;
}

type SortingOrder = 'asc' | 'desc';
export interface SortingKey {
  key: string;
  order: SortingOrder;
}

export interface TableProps extends Omit<BaseTableProps, "width"|"height"> {
  loading: boolean;
  enabledRowSelection: boolean;
  dataSource: any;
  rowSelection?: any;
  onColumnSort: (params: any) => any;
  sortBy: SortingKey;
  rowClassName: any;
  columns: any;
  itemsPerPage: number;
  cellHeight?: number;
  showFilters?: boolean;
  updateColumnWidth?: (params: any) => any;
  CellRenderer?:any;
  width?:number;
  height?:number;
  footerRenderer?:any;
  footerHeight?:number;
  enabledVirtualScrollbar?: boolean;
  useEstimatedRowHeight?: boolean;
}

export interface TableColumn extends BaseColumn {
  _id: any;
}

export class SelectionCell extends React.PureComponent<any, any> {
  _handleChange = (e: any) => {
    const { rowData, column } = this.props;
    const { onChange } = column;

    onChange(rowData);
  };

  render() {
    const
        { rowData, column } = this.props,
        { selectedRowKeys } = column;

    const checked = selectedRowKeys.includes(rowData.key);

    return (
      <input type="checkbox" checked={checked} onChange={this._handleChange} />
    );
  }
}

function Empty( props:any ) {
  if ( props.loading ) return null;
  return props.node || null;
}

export default class BaseTable extends React.Component<TableProps, any> {
  tableRef: any = createRef();
  private CellRenderer:React.FunctionComponent;
  private rootRef = React.createRef<HTMLDivElement>();
  private scrollEl: HTMLElement | null = null;        // scrollable body
  private filtersEl: HTMLElement | null = null;       // filters section
  private actionsBarEl: HTMLElement | null = null;    // actions bar at bottom
  private tableContentEl: HTMLElement | null = null;  // table content container

  private heightRaf: number | null = null;            // for requestAnimationFrame
  private pendingHeight: number | null = null;        // stores next height

  private resizeTimer: number | null = null;

  public constructor(props: any) {
    super(props);

    this.CellRenderer = props.CellRenderer || TableCell;

    this.state = {
      columns: props.columns,
      fullScreen: false,
      selectedRowKeys: props.rowSelection
        ? props.rowSelection.selectedRowKeys
        : [],
      cellHeight: props.cellHeight || 41.5,
      tableWidth: props.hasSidebar ? window.innerWidth - 200 : window.innerWidth,
      changedIds: new Set(),
      dynamicTableHeight: undefined as number | undefined,
      needsFillerRows: false
    }
  }

  private scheduleHeightUpdate = (h: number) => {
    this.pendingHeight = Math.max(0, Math.round(h));
    if (this.heightRaf != null) return;
    this.heightRaf = requestAnimationFrame(() => {
      this.heightRaf = null;
      const next = this.pendingHeight ?? 0;
      if (this.state.dynamicTableHeight !== next) {
        this.setState({ dynamicTableHeight: next });
      }
    });
  };

  private onInternalScroll = (e: any) => {
    if (!this.filtersEl || !this.tableContentEl || !this.scrollEl) return;
  
    const scrollTop = e?.target?.scrollTop ?? 0;
    const filtersHeight = this.filtersEl.offsetHeight;
    const effectiveFiltersHeight = filtersHeight + 45;
  
    const maxScroll = Math.max(0, this.scrollEl.scrollHeight - this.scrollEl.clientHeight);
    let shift = Math.min(scrollTop, effectiveFiltersHeight);
  
    if (maxScroll > 0 && maxScroll < effectiveFiltersHeight) {
      const ratio = Math.min(1, scrollTop / maxScroll);
      shift = ratio * effectiveFiltersHeight;
    }
  
    if (scrollTop > 0 && scrollTop >= maxScroll - 1) shift = effectiveFiltersHeight;
    if (scrollTop === 0) shift = 0;
  
    const navbarHeight = 60;
    const actionsHeight = this.actionsBarEl?.offsetHeight ?? 40;
    const staticOffsets = navbarHeight + actionsHeight;
  
    let newTableHeight = window.innerHeight - (effectiveFiltersHeight - shift) - staticOffsets;
    const minHeight = window.innerHeight - staticOffsets;
    if (newTableHeight < minHeight) {
      newTableHeight = minHeight;
    }
  
    const rowHeight = this.props.rowHeight || 40;
    const rowsHeight = this.props.dataSource.length * rowHeight;

    const needsFillerRows = rowsHeight < newTableHeight;
  
    if (!this.heightRaf) {
      this.heightRaf = requestAnimationFrame(() => {
        this.heightRaf = null;
  
        this.filtersEl!.style.transform = `translateY(${-shift}px)`;
        this.filtersEl!.style.opacity = String(1 - Math.min(1, shift / effectiveFiltersHeight));
        this.tableContentEl!.style.transform = `translateY(${-shift}px)`;
  
        if (this.actionsBarEl) this.actionsBarEl.style.transform = ``;
  
        this.setState((prev: any) => {
          const updates: any = {};
          if (prev.dynamicTableHeight !== newTableHeight) updates.dynamicTableHeight = newTableHeight;
          if (prev.needsFillerRows !== needsFillerRows) updates.needsFillerRows = needsFillerRows;
          return Object.keys(updates).length ? updates : null;
        });
      });
    }
    const header = document.querySelector(".main-header");
    if (header) {
      if (e.target.scrollTop > 0) {
        header.classList.add("slimmer");
      } else {
        header.classList.remove("slimmer");
      }
    }
  };
  
  componentDidMount() {
    window.addEventListener("resize", this.updateTableSize);

    this.filtersEl = this.props.filtersRef?.current || null;
    this.actionsBarEl = this.rootRef.current
      ?.closest(".abstract-table")
      ?.querySelector(".actions-bar-container") as HTMLElement;

    const root = this.rootRef.current;

    const scrollEl =
      (root?.querySelector(".BaseTable__body") as HTMLElement) ||
      (root?.querySelector(".BaseTable__table-main") as HTMLElement) ||
      (root?.querySelector(".BaseTable__table") as HTMLElement) ||
      null;

    if (scrollEl) {
      this.scrollEl = scrollEl;
      this.scrollEl.addEventListener("scroll", this.onInternalScroll, { passive: true });

      this.tableContentEl = root?.querySelector(".BaseTable") as HTMLElement;

      this.onInternalScroll({ target: scrollEl });
    }

    [this.filtersEl, this.tableContentEl].forEach((el) => {
      if (el) (el.style as any).willChange = "transform";
    });
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateTableSize);
    if (this.scrollEl) this.scrollEl.removeEventListener('scroll', this.onInternalScroll);
    if (this.heightRaf) {
      cancelAnimationFrame(this.heightRaf);
      this.heightRaf = null;
    }

    [this.filtersEl, this.actionsBarEl].forEach((el) => {
      if (el) {
        el.style.transform = '';
        el.style.opacity = '';
        (el.style as any).willChange = '';
      }
    });
  }

  getTableHeight = () => {
    if (typeof this.state.dynamicTableHeight === 'number') {
      return this.state.dynamicTableHeight;
    }
    const navbarHeight = 60;
    const actionsHeight = this.actionsBarEl?.offsetHeight ?? 40;
    const filtersHeight = this.props.filtersRef?.current?.offsetHeight ?? 0;
    return Math.max(0, window.innerHeight - navbarHeight - actionsHeight - filtersHeight);
  };

  updateTableSize = () => {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      this.setState({
        tableWidth: this.props.hasSidebar ? window.innerWidth - 200 : window.innerWidth,
      });
  
      if (this.scrollEl) {
        this.onInternalScroll({ target: this.scrollEl });
      } else {
        this.scheduleHeightUpdate(this.getTableHeight());
      }
      this.resizeTimer = null;
    }, 150);
  };

  componentDidUpdate(prevProps: any) {
    if (this.state.columns !== this.props.columns) {
      this.setState({ columns: this.props.columns });
    }

    if (
      this.props.rowSelection &&
      this.state.selectedRowKeys !== this.props.rowSelection.selectedRowKeys
    ) {
      this.setState({
        selectedRowKeys: this.props.rowSelection.selectedRowKeys,
      });
    }
  }

  checkboxHeaderCell = () => {
    const selectedKeys = this.props.rowSelection?.selectedRowKeys || [];
    const total = this.props.dataSource?.length || 0;
    const allSelected = total > 0 && selectedKeys.length === total;

    const _handleChange = (e: any) => {
      this.props.rowSelection?.onSelectAll(e.target.checked);
     };

    return (<input type="checkbox" onChange={_handleChange} checked={allSelected} />);
  };

  estimatedRowHeight = ({ rowData }: any) => {
    const rowTextLength = (rowData.col1 || '').length + (rowData.col2 || '').length;
    return Math.max(40, Math.ceil(rowTextLength / 20) * 30);
  };

  getPaddingRows = () => {
    const rowHeight = this.props.rowHeight || 40;
    const dataLength = this.props.dataSource.length;
  
    if (dataLength === 0) return [];
  
    const rowsHeight = dataLength * rowHeight;

    const containerHeight = this.tableContentEl?.clientHeight ?? this.getTableHeight();
  
    if (rowsHeight >= containerHeight) return [];
  
    const diff = containerHeight - rowsHeight;
  
    if (diff < rowHeight) return [];
  
    const fillerCount = Math.floor(diff / rowHeight);
  
    return Array.from({ length: fillerCount }).map((_, i) => ({
      key: `__filler__${i}`,
      id: `__filler__${i}`,
      __filler: true,
    }));
  };
  
  withFillerGuard = (renderer: any) => {
    return (props: any) => {
      if (props.rowData.__filler) {
        return (
          <div
            style={{
              height: "100%",
              width: "100%",
              opacity: 0,
              pointerEvents: "none",
            }}
          />
        );
      }
      return renderer(props);
    };
  };
  
  render() {
    const { hasSidebar, useEstimatedRowHeight } = this.props;
    const hasData = this.props.dataSource && this.props.dataSource.length > 0;

    let baseColumns = this.state.columns.map((c: any) => {
      let frozen;
      if (c.fixed === "left") frozen = BaseColumn.FrozenDirection.LEFT;
      if (c.fixed === "right") frozen = BaseColumn.FrozenDirection.RIGHT;
    
      const renderer = (row: any) => this.CellRenderer({ ...row, ...c });
    
      return {
        key: c.dataIndex,
        title: c.title,
        dataIndex: c.dataIndex,
        sortable: c.sorter,
        width: c.width ? c.width + 10 : 160,
        maxWidth: c.width ? c.width + 300 : 460,
        minWidth: 60,
        resizable: true,
        align: c.align,
        cellRenderer: this.withFillerGuard(renderer),
        renderId: renderId(c.render),
        frozen,
      };
    });
    
    if (this.props.enabledRowSelection) {
      baseColumns = [
        {
          key: "__selection__",
          width: 40,
          flexShrink: 0,
          resizable: false,
          frozen: BaseColumn.FrozenDirection.LEFT,
          cellRenderer: this.withFillerGuard((props: any) => (
            <SelectionCell {...props} />
          )),
          headerRenderer: this.checkboxHeaderCell,
          selectedRowKeys: this.state.selectedRowKeys,
          onChange: this.props.rowSelection?.onSelect,
        },
        ...baseColumns,
      ];
    }
    
    const showVirtualScrollBar = this.props.enabledVirtualScrollbar ?? true;

    return (
        <div ref={this.rootRef}>
          <Table
              ref={this.tableRef}
              width={this.props.width || this.state.tableWidth}
              height={this.props.height ?? this.getTableHeight() + 22} // 22 = scrollbar compensation
              disabled={this.props.loading}
              fixed
              columns={baseColumns}
              data={[
                ...this.props.dataSource.map((r: TableColumn) => ({
                  ...r,
                  id: r._id,
                  key: r._id,
                })),
                ...(hasData ? this.getPaddingRows() : [])
              ]}
              rowClassName={(data: any) => {
                const rowClass = this.props.rowClassName(data.rowData, data.rowIndex);
                let finalClass = rowClass;

                if (useEstimatedRowHeight) {
                  finalClass += ' flex-wrap-row';
                }

                if (this.props.enabledRowSelection) {
                  if (this.state.selectedRowKeys.includes(data.rowData.id)) {
                    finalClass += ' BaseTable__row--selected';
                  }
                }

                return finalClass;
              }}
              
              // fix to match antd
              onColumnSort={(data: any) => {
                // fix to cancel sorting on click
                if ((data.key === this.props.sortBy.key) && (this.props.sortBy.order === 'desc')) {
                  return this.props.onColumnSort({
                    column: undefined,
                    field: undefined,
                    order: undefined,
                  })
                }

                return this.props.onColumnSort({
                  column: data.column,
                  field: data.key,
                  order:
                      data.order === "asc"
                          ? "ascend"
                          : data.order === "desc"
                              ? "descend"
                              : data.order,
                })
              }
              }
              overscanRowCountnumber={100}
              sortBy={this.props.sortBy}
              className={this.props.className}
              rowHeight={useEstimatedRowHeight ? undefined : (this.props.rowHeight || 40)}
              headerHeight={40}
              onColumnResize={this.props.updateColumnWidth}
              overscanRowCountnumberdefaults={200}
              overlayRenderer={
                this.props.overlayRenderer ? this.props.overlayRenderer :
                () => this.props.loading ? <div className="loading-table-wrapper"><LoadingStar logo={true} animate={true} title={""} /></div> : null
              }
              onScroll={this.props.onScroll}
              emptyRenderer={<Empty node={this.props.emptyRenderer} loading={this.state.loading} />}
              footerHeight={this.props.footerHeight ?? 0}
              footerRenderer={this.props.footerRenderer}
              estimatedRowHeight={useEstimatedRowHeight ? this.estimatedRowHeight : undefined}
          />
          {showVirtualScrollBar && <VirtualScrolbar hasSidebar={hasSidebar} table={this.tableRef} />}
        </ div>
    );
  }
}
